/**
 * Tab content components for AdminDashboard. Each tab is a pure presentational
 * component that receives data + action callbacks as props. Extracting them
 * here keeps AdminDashboard.jsx focused on data fetching + tab routing and
 * brings its cyclomatic complexity down significantly.
 */
import { BarChart3, CheckCircle2, XCircle } from "lucide-react";

export function OverviewTab({ stats }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="brut-card p-6 bg-white">
        <div className="flex items-center gap-2 mb-4"><BarChart3 className="w-5 h-5" /><div className="overline">Revenue per category</div></div>
        <div className="space-y-2">
          {(stats?.revenue_per_category || []).map((r) => (
            <div key={r.category} className="flex justify-between font-mono text-sm border-b border-ink/20 pb-2">
              <span>{r.category}</span>
              <span className="font-bold">${r.revenue.toFixed(2)}</span>
            </div>
          ))}
          {!stats?.revenue_per_category?.length && <p className="font-mono text-xs text-muted-foreground">No revenue yet.</p>}
        </div>
      </div>
      <div className="brut-card p-6 bg-acid">
        <div className="overline">Snapshot</div>
        <div className="mt-3 space-y-2 font-mono text-sm">
          <div>Pending pros · <b>{stats?.pending_contractors}</b></div>
          <div>Total contractors · <b>{stats?.contractors}</b></div>
          <div>Customers · <b>{stats?.customers}</b></div>
          <div>Transactions · <b>{stats?.transactions}</b></div>
        </div>
      </div>
    </div>
  );
}

export function ContractorsTab({ contractors, onApprove, onReject }) {
  return (
    <div className="brut-card bg-white overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-ink text-acid"><tr>
          <th className="text-left p-3 font-mono uppercase">Name</th>
          <th className="text-left p-3 font-mono uppercase">Email</th>
          <th className="text-left p-3 font-mono uppercase">Status</th>
          <th className="text-left p-3 font-mono uppercase">Subscription</th>
          <th className="text-left p-3 font-mono uppercase">Credits</th>
          <th className="text-left p-3 font-mono uppercase">Actions</th>
        </tr></thead>
        <tbody>
          {contractors.map((c) => (
            <tr key={c.user_id} className="border-t-2 border-ink" data-testid={`row-${c.user_id}`}>
              <td className="p-3">{c.name}</td>
              <td className="p-3 font-mono text-xs">{c.email}</td>
              <td className="p-3"><span className={`font-mono text-xs border-2 border-ink px-2 py-1 ${c.contractor_approved ? "bg-acid" : "bg-white"}`}>{c.contractor_approved ? "APPROVED" : "PENDING"}</span></td>
              <td className="p-3 font-mono text-xs">{c.subscription_tier?.toUpperCase() || "—"}</td>
              <td className="p-3 font-mono text-xs">${(c.credits || 0).toFixed(2)}</td>
              <td className="p-3 space-x-2">
                {!c.contractor_approved && <button onClick={() => onApprove(c.user_id)} className="brut-btn-primary text-[10px] px-3 py-2" data-testid={`approve-${c.user_id}`}><CheckCircle2 className="w-3 h-3 mr-1" />Approve</button>}
                {c.contractor_approved && <button onClick={() => onReject(c.user_id)} className="brut-btn text-[10px] px-3 py-2" data-testid={`reject-${c.user_id}`}><XCircle className="w-3 h-3 mr-1" />Suspend</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TransactionsTab({ txns }) {
  return (
    <div className="brut-card bg-white overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-ink text-acid"><tr>
          <th className="text-left p-3 font-mono uppercase">Date</th>
          <th className="text-left p-3 font-mono uppercase">User</th>
          <th className="text-left p-3 font-mono uppercase">Amount</th>
          <th className="text-left p-3 font-mono uppercase">Type</th>
          <th className="text-left p-3 font-mono uppercase">Method</th>
          <th className="text-left p-3 font-mono uppercase">Status</th>
        </tr></thead>
        <tbody>
          {txns.map((t) => (
            <tr key={t.txn_id} className="border-t-2 border-ink">
              <td className="p-3 font-mono text-xs">{new Date(t.created_at).toLocaleString()}</td>
              <td className="p-3 font-mono text-xs">{t.email}</td>
              <td className="p-3 font-bold">${t.amount.toFixed(2)}</td>
              <td className="p-3 font-mono text-xs">{t.kind} / {t.ref}</td>
              <td className="p-3 font-mono text-xs uppercase">{t.method}</td>
              <td className="p-3"><span className={`font-mono text-xs border-2 border-ink px-2 py-1 ${t.payment_status === "paid" ? "bg-acid" : "bg-white"}`}>{t.payment_status}</span></td>
            </tr>
          ))}
          {!txns.length && <tr><td colSpan={6} className="p-6 text-center font-mono text-xs">No transactions yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export function JobsTab({ jobs }) {
  return (
    <div className="brut-card bg-white overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-ink text-acid"><tr>
          <th className="text-left p-3 font-mono uppercase">Title</th>
          <th className="text-left p-3 font-mono uppercase">Category</th>
          <th className="text-left p-3 font-mono uppercase">Location</th>
          <th className="text-left p-3 font-mono uppercase">Unlocks</th>
          <th className="text-left p-3 font-mono uppercase">Status</th>
        </tr></thead>
        <tbody>
          {jobs.map((j) => (
            <tr key={j.job_id} className="border-t-2 border-ink">
              <td className="p-3">{j.title}</td>
              <td className="p-3 font-mono text-xs">{j.category_name}</td>
              <td className="p-3 font-mono text-xs">{j.location}, {j.zip_code}</td>
              <td className="p-3 font-mono">{j.contractor_unlocks}</td>
              <td className="p-3"><span className="font-mono text-xs border-2 border-ink px-2 py-1 bg-acid">{j.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function DisputesTab({ disputes, onRefund, onReject }) {
  return (
    <div className="brut-card bg-white overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-ink text-acid"><tr>
          <th className="text-left p-3 font-mono uppercase">Date</th>
          <th className="text-left p-3 font-mono uppercase">Contractor</th>
          <th className="text-left p-3 font-mono uppercase">Job</th>
          <th className="text-left p-3 font-mono uppercase">Reason</th>
          <th className="text-left p-3 font-mono uppercase">Charged</th>
          <th className="text-left p-3 font-mono uppercase">Status</th>
          <th className="text-left p-3 font-mono uppercase">Actions</th>
        </tr></thead>
        <tbody>
          {disputes.map((d) => (
            <tr key={d.dispute_id} className="border-t-2 border-ink" data-testid={`dispute-row-${d.dispute_id}`}>
              <td className="p-3 font-mono text-xs">{new Date(d.created_at).toLocaleString()}</td>
              <td className="p-3 font-mono text-xs">{d.contractor_name}<br/><span className="opacity-60">{d.contractor_email}</span></td>
              <td className="p-3 text-sm">{d.job_title}</td>
              <td className="p-3 text-xs max-w-[300px]">{d.reason}</td>
              <td className="p-3 font-bold">${d.price_charged.toFixed(2)}</td>
              <td className="p-3"><span className={`font-mono text-xs border-2 border-ink px-2 py-1 ${d.status === "refunded" ? "bg-acid" : d.status === "rejected" ? "bg-white" : "bg-bone"}`}>{d.status}</span></td>
              <td className="p-3 space-x-2">
                {d.status === "open" && (
                  <>
                    <button onClick={() => onRefund(d.dispute_id)} className="brut-btn-primary text-[10px] px-3 py-2" data-testid={`refund-${d.dispute_id}`}>Refund</button>
                    <button onClick={() => onReject(d.dispute_id)} className="brut-btn text-[10px] px-3 py-2" data-testid={`reject-dispute-${d.dispute_id}`}>Reject</button>
                  </>
                )}
              </td>
            </tr>
          ))}
          {!disputes.length && <tr><td colSpan={7} className="p-6 text-center font-mono text-xs">No disputes filed.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export function QuizStatsTab({ quizStats }) {
  return (
    <div className="brut-card bg-white overflow-x-auto" data-testid="quiz-stats-table">
      <table className="w-full text-sm">
        <thead className="bg-ink text-acid"><tr>
          <th className="text-left p-3 font-mono uppercase">Scope</th>
          <th className="text-left p-3 font-mono uppercase">Urgency</th>
          <th className="text-left p-3 font-mono uppercase">Budget</th>
          <th className="text-left p-3 font-mono uppercase">Completions</th>
          <th className="text-left p-3 font-mono uppercase">Conversions</th>
          <th className="text-left p-3 font-mono uppercase">Rate</th>
        </tr></thead>
        <tbody>
          {(quizStats?.combos || []).map((c) => (
            <tr key={`${c.scope}-${c.urgency}-${c.budget}`} className="border-t-2 border-ink">
              <td className="p-3 font-mono text-xs uppercase">{c.scope}</td>
              <td className="p-3 font-mono text-xs uppercase">{c.urgency}</td>
              <td className="p-3 font-mono text-xs uppercase">{c.budget}</td>
              <td className="p-3 font-bold">{c.completions}</td>
              <td className="p-3 font-bold">{c.conversions}</td>
              <td className="p-3"><span className={`font-mono text-xs border-2 border-ink px-2 py-1 ${c.rate >= 30 ? "bg-acid" : c.rate >= 10 ? "bg-bone" : "bg-white"}`}>{c.rate}%</span></td>
            </tr>
          ))}
          {!quizStats?.combos?.length && (
            <tr><td colSpan={6} className="p-6 text-center font-mono text-xs">No quiz events yet. Once users start running the quiz, this table will populate.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
